var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { focusable } from 'tabbable';
import { KeyAttributeValue } from '@hawkins/helpers';
import { FloatingUI } from '../floatingUi';
import { useCombineRefs } from '../hooks';
import { MenuContext } from '../menu';
import { Paper } from '../paper';
var NestedMenuContext = React.createContext({
    closeOnAnyClick: undefined,
    isInNestedMenu: false,
    onAnyClick: undefined,
    recursivelyCloseMenu: undefined,
});
/**
 * A hook that returns callbacks, refs, and values for implementing a nested menu.
 * Supports infinite levels of nesting.
 */
export function useNestedMenu(_a) {
    var classes = _a.classes, closeOnAnyClickProp = _a.closeOnAnyClick, defaultNestedMenuOpen = _a.defaultNestedMenuOpen, disabled = _a.disabled, isClickable = _a.isClickable, nestedMenuContent = _a.nestedMenuContent, nestedMenuOpen = _a.nestedMenuOpen, nestedMenuProps = _a.nestedMenuProps, onAnyClickProp = _a.onAnyClick, onClick = _a.onClick, parentItemId = _a.parentItemId, floatingUiProps = _a.floatingUiProps;
    // Props
    var hasNestedMenuContent = !!nestedMenuContent;
    var isNestedMenuControlled = nestedMenuOpen !== undefined;
    var shouldSetInternalNestedMenuOpen = hasNestedMenuContent && !isNestedMenuControlled;
    // Refs
    var internalRootRef = React.useRef(null);
    var popperRef = React.useRef(null);
    var _b = __read(React.useState(null), 2), rootRefState = _b[0], setRootRefState = _b[1];
    var rootRef = useCombineRefs([internalRootRef, setRootRefState]);
    var isRootRefSet = !!rootRefState;
    // State
    var _c = __read(React.useState(defaultNestedMenuOpen), 2), internalNestedMenuOpen = _c[0], setInternalNestedMenuOpen = _c[1];
    var derivedNestedMenuOpen = nestedMenuOpen !== null && nestedMenuOpen !== void 0 ? nestedMenuOpen : internalNestedMenuOpen;
    // Ensure the nested menu is closed if it doesn't have content.
    React.useEffect(function () {
        if (!hasNestedMenuContent && !isNestedMenuControlled)
            setInternalNestedMenuOpen(false);
    }, [hasNestedMenuContent, isNestedMenuControlled]);
    // Context
    var _d = React.useContext(MenuContext), closeMenu = _d.closeMenu, closeOnSelect = _d.closeOnSelect;
    var _e = React.useContext(NestedMenuContext), closeOnAnyClickInherited = _e.closeOnAnyClick, isInNestedMenuInherited = _e.isInNestedMenu, onAnyClickInherited = _e.onAnyClick, recursivelyCloseMenuInherited = _e.recursivelyCloseMenu;
    var nestedMenuContext = React.useMemo(function () {
        var onAnyClickAugmented = function (ev, close) {
            onAnyClickProp === null || onAnyClickProp === void 0 ? void 0 : onAnyClickProp(ev);
            if ((close || (closeOnSelect && close !== false)) && shouldSetInternalNestedMenuOpen) {
                setInternalNestedMenuOpen(false);
            }
        };
        return {
            // Let the `closeOnAnyClick` prop (if set) override its inherited value.
            closeOnAnyClick: closeOnAnyClickProp !== null && closeOnAnyClickProp !== void 0 ? closeOnAnyClickProp : closeOnAnyClickInherited,
            isInNestedMenu: true,
            onAnyClick: onAnyClickInherited
                ? function (ev, close) {
                    onAnyClickAugmented(ev, close);
                    onAnyClickInherited(ev, close);
                }
                : onAnyClickAugmented,
            recursivelyCloseMenu: function (ev) {
                if (shouldSetInternalNestedMenuOpen)
                    setInternalNestedMenuOpen(false);
                if (recursivelyCloseMenuInherited) {
                    recursivelyCloseMenuInherited(ev);
                }
                else {
                    // Call `closeMenu` in case we're at the top level of a `Menu`.
                    closeMenu(ev);
                }
            },
        };
    }, [
        closeMenu,
        closeOnAnyClickInherited,
        closeOnAnyClickProp,
        closeOnSelect,
        onAnyClickInherited,
        onAnyClickProp,
        recursivelyCloseMenuInherited,
        shouldSetInternalNestedMenuOpen,
    ]);
    var closeOnAnyClickCurrent = nestedMenuContext.closeOnAnyClick, onAnyClickCurrent = nestedMenuContext.onAnyClick, recursivelyCloseMenuCurrent = nestedMenuContext.recursivelyCloseMenu;
    // Callbacks
    var onBlur = React.useCallback(function (ev) {
        var _a;
        if (!shouldSetInternalNestedMenuOpen || disabled)
            return;
        if (
        // Only close the nested menu if focus is transferring away from the parent menu item.
        // Otherwise, the nested menu would be closed incorrectly when a nested menu item has a nested menu.
        ev.target === internalRootRef.current &&
            ev.relatedTarget instanceof Element &&
            !((_a = popperRef.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget))) {
            setInternalNestedMenuOpen(false);
        }
    }, [disabled, shouldSetInternalNestedMenuOpen]);
    var onFocus = React.useCallback(function (ev) {
        var _a;
        if (!shouldSetInternalNestedMenuOpen || disabled)
            return;
        if (
        // Only open the nested menu if focus is transferring in from outside the parent menu item.
        ev.target === internalRootRef.current &&
            ev.relatedTarget instanceof Element &&
            !((_a = popperRef.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget))) {
            setInternalNestedMenuOpen(true);
        }
    }, [disabled, shouldSetInternalNestedMenuOpen]);
    var onKeyDown = React.useCallback(function (ev) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (!(ev.target instanceof Element))
            return;
        if (ev.key === KeyAttributeValue.ARROW_DOWN) {
            // If inside a nested menu, shift focus to the next nested menu item.
            if ((_a = popperRef.current) === null || _a === void 0 ? void 0 : _a.contains(ev.target)) {
                // Prevent the page from scrolling.
                ev.preventDefault();
                var focusableEls = focusable(popperRef.current);
                var targetIdx = focusableEls.findIndex(function (el) { return el === ev.target; });
                if (targetIdx === -1)
                    return;
                (_b = focusableEls[targetIdx >= focusableEls.length - 1 ? 0 : targetIdx + 1]) === null || _b === void 0 ? void 0 : _b.focus();
            }
        }
        else if (ev.key === KeyAttributeValue.ARROW_LEFT) {
            // If inside a nested menu, shift focus to the parent menu item.
            if ((_c = popperRef.current) === null || _c === void 0 ? void 0 : _c.contains(ev.target)) {
                // Prevent the page from scrolling.
                ev.preventDefault();
                (_d = internalRootRef.current) === null || _d === void 0 ? void 0 : _d.focus();
            }
        }
        else if (ev.key === KeyAttributeValue.ARROW_RIGHT) {
            // If focused on the parent menu item, shift focus to the first nested menu item.
            if (hasNestedMenuContent && ev.target === internalRootRef.current) {
                // Prevent the page from scrolling.
                ev.preventDefault();
                if (!popperRef.current)
                    return;
                (_e = focusable(popperRef.current)[0]) === null || _e === void 0 ? void 0 : _e.focus();
            }
        }
        else if (ev.key === KeyAttributeValue.ARROW_UP) {
            // If inside a nested menu, shift focus to the previous nested menu item.
            if ((_f = popperRef.current) === null || _f === void 0 ? void 0 : _f.contains(ev.target)) {
                // Prevent the page from scrolling.
                ev.preventDefault();
                var focusableEls = focusable(popperRef.current);
                var targetIdx = focusableEls.findIndex(function (el) { return el === ev.target; });
                if (targetIdx === -1)
                    return;
                (_g = focusableEls[targetIdx <= 0 ? focusableEls.length - 1 : targetIdx - 1]) === null || _g === void 0 ? void 0 : _g.focus();
            }
        }
        else if (ev.key === KeyAttributeValue.ESCAPE) {
            if (!shouldSetInternalNestedMenuOpen || disabled)
                return;
            // Close the nested menu.
            if (internalRootRef.current === ev.target) {
                setInternalNestedMenuOpen(false);
            }
            else if (
            // If we're inside a nested menu, only close the nested menu if the focused nested menu item
            // does NOT have an open menu (otherwise, close the nested menu item's nested menu).
            ((_h = popperRef.current) === null || _h === void 0 ? void 0 : _h.contains(ev.target)) &&
                (!ev.target.getAttribute('aria-haspopup') ||
                    ev.target.getAttribute('aria-haspopup') === 'false' ||
                    !ev.target.getAttribute('aria-expanded') ||
                    ev.target.getAttribute('aria-expanded') === 'false')) {
                (_j = internalRootRef.current) === null || _j === void 0 ? void 0 : _j.focus();
                setInternalNestedMenuOpen(false);
            }
        }
        else if (ev.key === KeyAttributeValue.TAB) {
            // Only recursively close the menu if we're inside a nested menu.
            // Otherwise, `Menu` would handle closing itself (if we're inside a `Menu`).
            // If we're not inside a nested menu or a `Menu`, then nothing needs to be closed.
            if ((_k = popperRef.current) === null || _k === void 0 ? void 0 : _k.contains(ev.target)) {
                // When `Menu` is involved, allowing the default tabbing behavior to take place
                // can get buggy because `Menu` manipulates focus, so prevent the default tabbing behavior.
                // This will cause focus to end up on the root menu item (or the `Menu` trigger if there is one)
                // rather than automatically shifting forward or backward (if `Tab+Shift` was pressed).
                //
                // TODO: Improve this?
                ev.preventDefault();
                recursivelyCloseMenuCurrent === null || recursivelyCloseMenuCurrent === void 0 ? void 0 : recursivelyCloseMenuCurrent(ev);
            }
        }
    }, [disabled, hasNestedMenuContent, recursivelyCloseMenuCurrent, shouldSetInternalNestedMenuOpen]);
    var onMouseEnter = React.useCallback(function () {
        if (!shouldSetInternalNestedMenuOpen || disabled)
            return;
        setInternalNestedMenuOpen(true);
    }, [disabled, shouldSetInternalNestedMenuOpen]);
    var onMouseLeave = React.useCallback(function () {
        if (!shouldSetInternalNestedMenuOpen || disabled)
            return;
        setInternalNestedMenuOpen(false);
    }, [disabled, shouldSetInternalNestedMenuOpen]);
    var onClickAugmented = React.useMemo(function () {
        if (isClickable) {
            return function (ev) {
                onClick === null || onClick === void 0 ? void 0 : onClick(ev);
                onAnyClickCurrent === null || onAnyClickCurrent === void 0 ? void 0 : onAnyClickCurrent(ev, closeOnAnyClickCurrent);
                if (closeOnSelect && closeOnAnyClickCurrent !== false)
                    recursivelyCloseMenuCurrent === null || recursivelyCloseMenuCurrent === void 0 ? void 0 : recursivelyCloseMenuCurrent(ev);
            };
        }
        else if (shouldSetInternalNestedMenuOpen) {
            return function () { return setInternalNestedMenuOpen(true); };
        }
        return undefined;
    }, [
        closeOnAnyClickCurrent,
        closeOnSelect,
        isClickable,
        onAnyClickCurrent,
        onClick,
        recursivelyCloseMenuCurrent,
        shouldSetInternalNestedMenuOpen,
    ]);
    return {
        derivedNestedMenuOpen: derivedNestedMenuOpen,
        isInNestedMenu: isInNestedMenuInherited,
        nestedMenuNode: hasNestedMenuContent ? (_jsx(NestedMenuContext.Provider, __assign({ value: nestedMenuContext }, { children: _jsx(FloatingUI, __assign({ anchor: internalRootRef.current, className: classes === null || classes === void 0 ? void 0 : classes.nestedMenuPopper, open: isRootRefSet && derivedNestedMenuOpen, placement: "right-start", styled: false, ref: popperRef, offset: 0 }, floatingUiProps, { children: _jsx(Paper, __assign({ "aria-labelledby": (nestedMenuProps === null || nestedMenuProps === void 0 ? void 0 : nestedMenuProps['aria-label']) ? undefined : parentItemId, className: classes === null || classes === void 0 ? void 0 : classes.nestedMenu, component: "ul", elevation: 2, role: "menu" }, nestedMenuProps, { children: nestedMenuContent })) })) }))) : null,
        onClickAugmented: onClickAugmented,
        rootCallbacks: { onBlur: onBlur, onFocus: onFocus, onKeyDown: onKeyDown, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
        rootRef: rootRef,
    };
}
useNestedMenu.displayName = 'useNestedMenu';
